import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain, Eye } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAVOUR_COLOR = {
  manual:    '#a855f7',
  scheduled: '#10b981',
  auto:      '#38bdf8',
};

const CONF_COLOR = {
  HIGH:   '#10b981',
  MEDIUM: '#f59e0b',
  LOW:    '#ef4444',
};

const AdminRoutingPage = () => {
  const [window, setWindow] = useState(7);
  const [data, setData] = useState(null);
  const [tuning, setTuning] = useState(null);
  const [adapting, setAdapting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (w) => {
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/growth/routing`, { params: { window_days: w } });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 401) setError('Please sign in');
      else if (e.response?.status === 403) setError('Admin access required');
      else setError('Could not load routing data');
    }
    try {
      const r = await axios.get(`${API}/admin/growth/routing/tuning`);
      setTuning(r.data);
    } catch { setTuning(null); }
  }, []);

  useEffect(() => { load(window); }, [load, window]);

  const runAdaptNow = async () => {
    setAdapting(true);
    try {
      await axios.post(`${API}/admin/growth/routing/adapt-now`);
      await load(window);
    } catch { /* silently ignore */ }
    setAdapting(false);
  };

  if (error) return <FullscreenMessage testId="routing-error">{error}</FullscreenMessage>;
  if (!data)  return <FullscreenMessage testId="routing-loading">Loading…</FullscreenMessage>;

  const { recent_decisions, reason_breakdown, confidence_distribution, strategy_counts, shadow_mode, fallback_count } = data;
  const totalDecisions = (strategy_counts.manual || 0) + (strategy_counts.scheduled || 0) + (strategy_counts.auto || 0);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-routing-page">
      <div className="max-w-5xl mx-auto px-5 py-8 space-y-8">
        <header className="flex items-center justify-between gap-4">
          <Link to="/admin/growth" className="flex items-center gap-2 text-white/60 hover:text-white text-sm font-bold">
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>
          <div className="flex items-center gap-3">
            {[7, 30, 90].map((w) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider ${w === window ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'}`}
                data-testid={`routing-window-${w}`}
              >{w}d</button>
            ))}
          </div>
        </header>

        <div>
          <div className="flex items-center gap-3 mb-2">
            <Brain size={22} weight="fill" className="text-[#C29D52]" />
            <h1 className="text-3xl font-black tracking-tight">Auto-routing</h1>
            {shadow_mode && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[2px] text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-1" data-testid="routing-shadow-badge">
                <Eye size={10} weight="fill" />Shadow
              </span>
            )}
          </div>
          <p className="text-white/55 text-sm">
            {shadow_mode
              ? "Observing decisions — system would pick this, but no boosts are being routed yet."
              : "System is actively routing between manual, scheduled, and auto based on flavour performance."}
          </p>
        </div>

        {/* Strategy counts */}
        <section>
          <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-3">Strategy distribution</h2>
          <div className="grid grid-cols-3 gap-3" data-testid="routing-strategy-counts">
            {["manual", "scheduled", "auto"].map((src) => {
              const count = strategy_counts[src] || 0;
              const pct = totalDecisions ? Math.round(count * 100 / totalDecisions) : 0;
              return (
                <div key={src} className="rounded-2xl p-4 border border-white/10 bg-white/[0.02]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: FLAVOUR_COLOR[src] }} />
                    <span className="text-xs font-bold text-white/70 capitalize">{src}</span>
                  </div>
                  <div className="text-2xl font-black">{count}</div>
                  <div className="text-[11px] text-white/40">{pct}% of decisions</div>
                </div>
              );
            })}
          </div>
          {fallback_count > 0 && (
            <p className="text-[11px] text-amber-300/70 mt-3" data-testid="routing-fallback-note">
              {fallback_count} decision{fallback_count === 1 ? '' : 's'} used fallback (low confidence or tie) — data will sharpen over time.
            </p>
          )}
        </section>

        {/* Confidence distribution */}
        <section>
          <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-3">Confidence levels</h2>
          <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.02] space-y-2" data-testid="routing-confidence">
            {["HIGH", "MEDIUM", "LOW"].map((k) => {
              const n = confidence_distribution[k] || 0;
              const pct = totalDecisions ? Math.round(n * 100 / totalDecisions) : 0;
              return (
                <div key={k} className="flex items-center gap-3 text-sm">
                  <span className="w-16 text-xs font-bold text-white/70">{k}</span>
                  <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: CONF_COLOR[k] }} />
                  </div>
                  <span className="text-xs text-white/55 w-14 text-right">{pct}% ({n})</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Reason breakdown */}
        {Object.keys(reason_breakdown).length > 0 && (
          <section>
            <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-3">Triggered by</h2>
            <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.02] flex flex-wrap gap-3" data-testid="routing-reasons">
              {Object.entries(reason_breakdown).map(([reason, count]) => (
                <div key={reason} className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10">
                  <span className="text-xs font-bold text-white/85">{reason}</span>
                  <span className="ml-2 text-xs text-white/50">{count}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Live feed */}
        <section>
          <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-3">Decision feed (last 50)</h2>
          {recent_decisions.length === 0 ? (
            <div className="rounded-2xl p-8 border border-white/10 bg-white/[0.02] text-center text-white/40 text-sm" data-testid="routing-empty-feed">
              No decisions yet. Data will appear as the system observes auto-boost triggers.
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5" data-testid="routing-decision-feed">
              {recent_decisions.slice(0, 50).map((d) => (
                <DecisionRow key={d.id || d.created_at} d={d} />
              ))}
            </div>
          )}
        </section>

        {/* Adaptive tuning */}
        {tuning && (
          <section data-testid="routing-tuning-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50" data-testid="routing-tuning-heading">
                  Adaptive tuning{tuning.adaptive_tuning_enabled === false ? ' (learning only)' : ''}
                </h2>
                <p className="text-[11px] text-white/40 mt-0.5">
                  {tuning.adaptive_tuning_enabled === false
                    ? 'Paused — observing decisions without mutating thresholds. All logs and shadow routing remain active.'
                    : `Thresholds adapt slowly via EMA (${tuning.ema_old}×old + ${tuning.ema_new}×new). Clamped to [${tuning.clamps.confidence[0]}, ${tuning.clamps.confidence[1]}].`}
                </p>
              </div>
              <button
                onClick={runAdaptNow}
                disabled={adapting || tuning.adaptive_tuning_enabled === false}
                title={tuning.adaptive_tuning_enabled === false ? 'Adaptive tuning is currently in learning-only mode' : undefined}
                className="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider text-white/85 border border-white/15 hover:bg-white/[0.04] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="routing-adapt-now"
              >
                {adapting ? 'Adapting…' : 'Adapt now'}
              </button>
            </div>
            {tuning.tuning.length === 0 ? (
              <div className="rounded-2xl p-6 border border-white/10 bg-white/[0.02] text-center text-white/40 text-sm" data-testid="tuning-empty">
                No adjustments yet. Needs ≥{tuning.min_decisions} decisions per slot with misfire impact ≥{tuning.impact_gate}.
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5" data-testid="tuning-rows">
                {tuning.tuning.slice(0, 12).map((row) => (
                  <TuningRow key={`${row.dow}-${row.hour}`} row={row} />
                ))}
              </div>
            )}
            {tuning.recent_adjustments.length > 0 && (
              <div className="mt-4">
                <h3 className="text-[10px] font-black tracking-[2px] uppercase text-white/40 mb-2">Recent adjustments</h3>
                <div className="space-y-1" data-testid="tuning-log">
                  {tuning.recent_adjustments.slice(0, 8).map((a) => (
                    <div key={a.id} className="text-[11px] text-white/55 px-3 py-1.5 rounded-md bg-white/[0.02] border border-white/5 flex items-center gap-2">
                      <span className="font-bold text-white/70 shrink-0">{DOW_SHORT[a.dow]} {a.hour}:00</span>
                      <span>threshold {a.from_threshold.toFixed(2)} → {a.to_threshold.toFixed(2)}</span>
                      <span className="ml-auto text-white/35">misfire {Math.round(a.misfire_rate * 100)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

const DOW_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TuningRow = ({ row }) => {
  const baseThr = 0.6;
  const delta = (row.confidence_threshold - baseThr);
  return (
    <div className="px-4 py-3 flex items-center gap-3 text-[11px]" data-testid={`tuning-row-${row.dow}-${row.hour}`}>
      <span className="text-white/70 font-bold shrink-0 w-20">{DOW_SHORT[row.dow]} {row.hour}:00</span>
      <span className="text-white/55 shrink-0">
        conf <span className="text-white font-black">{row.confidence_threshold.toFixed(2)}</span>
        {delta !== 0 && (
          <span className={`ml-1 text-[10px] ${delta > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
            {delta > 0 ? '↑' : '↓'}{Math.abs(delta).toFixed(2)}
          </span>
        )}
      </span>
      <span className="text-white/35 shrink-0">·</span>
      <span className="text-white/55 flex items-center gap-2 shrink-0">
        <span style={{ color: FLAVOUR_COLOR.manual }}>M {row.bias.manual.toFixed(2)}</span>
        <span style={{ color: FLAVOUR_COLOR.scheduled }}>S {row.bias.scheduled.toFixed(2)}</span>
        <span style={{ color: FLAVOUR_COLOR.auto }}>A {row.bias.auto.toFixed(2)}</span>
      </span>
      <span className="ml-auto text-[10px] text-white/35 shrink-0">
        misfire {row.last_misfire_rate != null ? Math.round(row.last_misfire_rate * 100) : 0}%
      </span>
    </div>
  );
};

const DecisionRow = ({ d }) => {
  const time = d.created_at ? new Date(d.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const strat = d.chosen_strategy;
  return (
    <div className="px-4 py-3 flex items-center gap-3 text-[11px]" data-testid={`decision-row-${d.id}`}>
      <span className="text-white/40 shrink-0 w-12">{time}</span>
      <span className="shrink-0 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full min-w-[68px] text-center" style={{ color: FLAVOUR_COLOR[strat], background: `${FLAVOUR_COLOR[strat]}15`, border: `1px solid ${FLAVOUR_COLOR[strat]}40` }}>
        {strat}
      </span>
      <span className="text-white/55 shrink-0 truncate">{d.reason || 'trigger'}</span>
      <span className="text-white/40 shrink-0 text-[10px]">n={d.sample_size || 0}</span>
      <span className="ml-auto text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: CONF_COLOR[d.confidence_label], background: `${CONF_COLOR[d.confidence_label]}12` }}>
        {d.confidence_label}
      </span>
      {d.fallback && (
        <span className="text-[9px] font-black uppercase tracking-wider text-amber-300/80" title="Used fallback strategy">FB</span>
      )}
    </div>
  );
};

const FullscreenMessage = ({ children, testId }) => (
  <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center" data-testid={testId}>
    <p className="text-white/60 text-sm">{children}</p>
  </div>
);

export default AdminRoutingPage;
